// Display helpers for the account roadmap. The sequence, the windows and their state all arrive computed from
// the API (ADR 0003 §4); nothing here decides anything — it only puts the API's answer into words and into the
// geometry of the timeline rail.
import type { RoadmapLine, RoadmapState, RoadmapWindow } from '../types/accounts'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** YYYY-MM-DD → "Sep 10, 2026", without touching the local time zone. */
export function roadmapDate(iso: string | null): string {
  if (!iso) return '—'
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number)
  return Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d) ? iso : `${MONTHS[m - 1]} ${d}, ${y}`
}

/** Days between two YYYY-MM-DD dates, on the calendar, ignoring time zones. */
export function daysBetween(from: string, to: string): number {
  const a = Date.parse(`${from.slice(0, 10)}T00:00:00Z`)
  const b = Date.parse(`${to.slice(0, 10)}T00:00:00Z`)
  return Number.isNaN(a) || Number.isNaN(b) ? 0 : Math.round((b - a) / 86_400_000)
}

/** How the window reads, in a line: "opens Dec 9, 2026 · in 90 days", "open · 40 days left", "closed". */
export function windowLabel(line: RoadmapLine): string {
  const { item, window: w } = line
  switch (w.state) {
    case 'waiting':
      return `Opens ${roadmapDate(item.windowOpens)} · in ${w.daysUntilOpen} day${w.daysUntilOpen === 1 ? '' : 's'}`
    case 'open':
      return w.daysLeft == null ? 'Open, with no closing date' : `Open now · ${w.daysLeft} day${w.daysLeft === 1 ? '' : 's'} left`
    case 'closing':
      return `Closing · ${w.daysLeft} day${w.daysLeft === 1 ? '' : 's'} left`
    case 'closed':
      return `Window closed ${roadmapDate(item.windowCloses)}`
    default:
      return 'No window set'
  }
}

/** The tone a window carries: the callout tint on the panel and the rail. */
export function windowTone(w: RoadmapWindow): 'ok' | 'warn' | 'danger' | 'muted' {
  if (w.state === 'open') return 'ok'
  if (w.state === 'closing') return 'warn'
  if (w.state === 'closed') return 'danger'
  return 'muted'
}

/** What each state means, said once, in the person's language. */
export const STATE_WORDS: Record<RoadmapState, string> = {
  proposed: 'Proposed',
  planned: 'Planned',
  open: 'Round open',
  done: 'Done',
  dropped: 'Dropped',
}

/** A round nobody has taken on yet. Anything else is the person's, and a re-propose leaves it alone. */
export const isProposed = (state: RoadmapState) => state === 'proposed'

export interface RailBar {
  id: string
  label: string
  state: RoadmapState
  tone: 'ok' | 'warn' | 'danger' | 'muted'
  /** 0..1 across the rail */
  from: number
  to: number
}

/**
 * The timeline rail: every round with a window, laid out left to right across the span the roadmap covers
 * (today at the left edge when the roadmap runs forward). Rounds with no window are left off the rail —
 * they have nothing to place. Returns null when there is nothing to draw.
 */
export function rail(lines: RoadmapLine[], today: string): { bars: RailBar[]; months: { at: number; label: string }[]; todayAt: number } | null {
  const dated = lines.filter((l) => l.item.windowOpens)
  if (dated.length === 0) return null
  const starts = dated.map((l) => daysBetween(today, l.item.windowOpens!))
  const ends = dated.map((l) => (l.item.windowCloses ? daysBetween(today, l.item.windowCloses) : daysBetween(today, l.item.windowOpens!) + 30))
  const min = Math.min(0, ...starts)
  const max = Math.max(...ends, min + 30)
  const span = max - min || 1
  const at = (day: number) => (day - min) / span

  const bars: RailBar[] = dated.map((l, i) => ({
    id: l.item.roadmapItemId,
    label: l.item.serviceLine,
    state: l.item.state,
    tone: windowTone(l.window),
    from: at(starts[i]),
    to: Math.max(at(starts[i]) + 0.01, at(ends[i])),
  }))

  // a tick every quarter across the span, labelled with the month it lands in
  const months: { at: number; label: string }[] = []
  const base = new Date(`${today.slice(0, 10)}T00:00:00Z`)
  for (let day = Math.ceil(min / 90) * 90; day <= max; day += 90) {
    const d = new Date(base.getTime() + day * 86_400_000)
    months.push({ at: at(day), label: `${MONTHS[d.getUTCMonth()]} ’${String(d.getUTCFullYear()).slice(2)}` })
  }
  return { bars, months, todayAt: at(0) }
}
